/*
 * Description:
 * ---------------------
 * Copy this box's firewall to other boxes of the same customer: its settings
 * (on or off, management networks, known-bad lists, countries, rules, log
 * server) and its device groups' definitions (name, places, blocked kinds of
 * traffic), matched at each box by name. A box keeps its own devices, and a
 * group keeps its own speed limits and lines. Each box confirms its own change
 * the way this screen does, after a fresh read of it answers, so a box the
 * copy would cut off puts its settings back by itself. One box at a time, each
 * with its own result. Wording in firewallWords.js.
 *
 * ! file components/FirewallCopy.js
 */
import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { nodeApiUrlFor } from "../serverApi";
import { confirmChange } from "../utils/confirmChange";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { isAnchorNode, isMobileNode } from "../utils/nodeKind";
import * as W from "./firewallWords";
import "./RemoteDevices.css";

const sameName = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/* The boxes this one's firewall may be copied to: the customer's other
   boxes. A box no customer holds has none. */
export function copyTargets(nodes, here) {
  const domain = (nodes || []).find((n) => n.node_id === here)?.domain;
  if (!domain) return [];
  return nodes.filter((n) => n.node_id !== here && n.domain === domain && !isAnchorNode(n) && !isMobileNode(n))
    .sort((a, b) => a.node_id.localeCompare(b.node_id));
}

/* The groups this box defines for the firewall: those holding devices to
   places or blocking kinds of traffic. */
export const firewallGroups = (profiles) =>
  (profiles || []).filter((p) => (p.destinations || []).length || Object.values(p.classes || {}).some((r) => r.block));

/* The other groups the rules name: copied as names alone, so a rule finds
   its group at a box that has none of that name, which then picks its own
   devices for it. */
export const namedGroups = (profiles, rules) => {
  const defined = firewallGroups(profiles);
  return (profiles || []).filter((p) => !defined.includes(p) && (rules || []).some((r) => r.group === p.id));
};

/* A group as it is put at another box: this box's places and blocked kinds
   over the other box's own profile of the same name, which keeps its limits,
   lines and other classes. */
export function groupAt(src, there) {
  const classes = Object.fromEntries(Object.entries(there?.classes || {}).filter(([, r]) => !r.block));
  for (const [k, r] of Object.entries(src.classes || {})) if (r.block) classes[k] = { block: true };
  const own = { destinations: src.destinations || [], classes };
  return there ? { ...there, ...own } : { name: src.name, ...own };
}

/* This box's rules as another box takes them: a group named by the id of the
   group with its name there. A rule naming a group the other box lacks is
   left out. */
export function rulesAt(rules, here, there) {
  const kept = [];
  const left = [];
  for (const r of rules || []) {
    if (!r.group) {
      kept.push(r);
      continue;
    }
    const name = (here || []).find((p) => p.id === r.group)?.name;
    const match = name && (there || []).find((p) => sameName(p.name, name));
    if (match) kept.push({ ...r, group: match.id });
    else left.push(r);
  }
  return { kept, left };
}

/* One request to one box: its answer, or an Error in its own words. */
async function call(node, method, path, body) {
  const res = await fetchWithTimeout(nodeApiUrlFor(node)(path), {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).catch(() => null);
  if (!res) throw new Error(W.COPY.noAnswer);
  if (!res.ok) throw new Error((await res.text().catch(() => "")).trim() || `The box refused it (HTTP ${res.status}).`);
  return res.json().catch(() => null);
}

/* Copies to one box: the groups first, so the rules can name them; then the
   settings, confirmed once the box answers a fresh read. Answers what the
   operator should know beyond "copied". */
export async function copyTo(node, { settings, groups, here }) {
  const fw = await call(node, "GET", "/firewall");
  if (!fw?.available) throw new Error(fw?.reason || W.COPY.noFirewall);
  if (settings && !Array.isArray(fw.rules)) throw new Error(W.COPY.older);
  const needGroups = groups || (settings?.rules || []).some((r) => r.group);
  let controls = needGroups ? await call(node, "GET", "/devices/controls") : null;
  if (groups) {
    if (!controls?.available) throw new Error(controls?.reason || W.COPY.noGroups);
    const there = controls.profiles || [];
    for (const src of firewallGroups(here)) {
      await call(node, "PUT", "/devices/controls/profile", groupAt(src, there.find((p) => sameName(p.name, src.name))));
    }
    for (const src of namedGroups(here, settings?.rules)) {
      if (!there.some((p) => sameName(p.name, src.name))) {
        await call(node, "PUT", "/devices/controls/profile", { name: src.name, destinations: [], classes: {} });
      }
    }
    controls = await call(node, "GET", "/devices/controls");
  }
  const notes = [];
  if (settings) {
    const { kept, left } = rulesAt(settings.rules, here, controls?.profiles);
    if (left.length) notes.push(W.COPY.leftOut(left));
    const written = await call(node, "PUT", "/firewall", { ...settings, rules: kept });
    await confirmChange(nodeApiUrlFor(node), written, "/firewall", "/firewall/confirm");
  }
  return notes;
}

export default function FirewallCopy({ selectedNode, nodes, settings, profiles, onClose }) {
  const ref = useRef(null);
  const targets = copyTargets(nodes, selectedNode);
  const [what, setWhat] = useState({ settings: true, groups: firewallGroups(profiles).length > 0 });
  const [picked, setPicked] = useState([]);
  const [results, setResults] = useState({}); // node id → { state, text }
  const [running, setRunning] = useState(false);
  useEffect(() => { ref.current?.focus(); }, []);
  const live = targets.filter((n) => !n.stale);
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const run = async () => {
    setRunning(true);
    for (const id of picked) {
      setResults((r) => ({ ...r, [id]: { state: "busy", text: W.COPY.copying } }));
      try {
        const notes = await copyTo(id, { settings: what.settings ? settings : null, groups: what.groups, here: profiles });
        setResults((r) => ({ ...r, [id]: { state: "ok", text: [W.COPY.done, ...notes].join(" ") } }));
      } catch (e) {
        setResults((r) => ({ ...r, [id]: { state: "bad", text: `${W.COPY.failed}: ${e.message}` } }));
      }
    }
    setRunning(false);
  };
  const ran = Object.keys(results).length > 0 && !running;
  return (
    <>
      <div className="drawer-backdrop" onClick={running ? undefined : onClose} aria-hidden="true" />
      <div className="rd-sheet fw-sheet" role="dialog" aria-modal="true" aria-labelledby="fw-copy-title" ref={ref} tabIndex={-1}
        onKeyDown={(e) => e.key === "Escape" && !running && onClose()}>
        <header className="rd-sheet-head">
          <div className="rd-sheet-heading">
            <span className="rd-sheet-eyebrow">{selectedNode}</span>
            <h2 id="fw-copy-title">{W.COPY.title}</h2>
          </div>
          <button type="button" className="rd-sheet-close" onClick={onClose} disabled={running} aria-label="Close"><FiX size={18} /></button>
        </header>
        <div className="rd-sheet-body">
          <p className="fw-muted">{W.COPY.means}</p>
          <section className="fw-field">
            <h3>What to copy</h3>
            {Object.entries(W.COPY.what).map(([k, w]) => (
              <label key={k} className="fw-check">
                <input type="checkbox" checked={what[k]} disabled={running} onChange={() => setWhat({ ...what, [k]: !what[k] })} />
                <span>
                  <strong>{w.label}</strong>
                  <small>{k === "settings" ? W.COPY.summary(settings)
                    : W.COPY.groupsSummary(firewallGroups(profiles), what.settings ? namedGroups(profiles, settings.rules) : [])}</small>
                </span>
              </label>
            ))}
            {what.settings && (settings.rules || []).some((r) => r.group) && <p className="fw-muted">{W.COPY.groupsRulesNote}</p>}
          </section>
          <section className="fw-field">
            <h3>{W.COPY.sites}</h3>
            {targets.length === 0 && <p className="fw-muted">{W.COPY.none}</p>}
            {live.length > 1 && (
              <button type="button" className="fw-link" disabled={running}
                onClick={() => setPicked(picked.length === live.length ? [] : live.map((n) => n.node_id))}>
                {picked.length === live.length ? W.COPY.clearAll : W.COPY.pickAll}
              </button>
            )}
            <ul className="fw-copy-sites">
              {targets.map((n) => {
                const res = results[n.node_id];
                return (
                  <li key={n.node_id}>
                    <label className="fw-check">
                      <input type="checkbox" checked={picked.includes(n.node_id)} disabled={running || n.stale} onChange={() => toggle(n.node_id)} />
                      <span><strong>{n.node_id}</strong>{n.stale && <small>{W.COPY.offline}</small>}</span>
                    </label>
                    {res && <span className={`fw-copy-result is-${res.state}`} role="status">{res.text}</span>}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
        <footer className="rd-sheet-foot">
          <button type="button" className="fw-btn" onClick={onClose} disabled={running}>{ran ? "Close" : "Cancel"}</button>
          <button type="button" className="fw-btn is-primary" onClick={run}
            disabled={running || !picked.length || (!what.settings && !what.groups)}>
            {running ? W.COPY.copying : W.COPY.go(picked.length)}
          </button>
        </footer>
      </div>
    </>
  );
}
